import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { categories, movies } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);
  const categoryMovies = movies.filter((m) => m.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `悟空影视${category.name} - ${category.description}`,
    description: `悟空影视${category.name}大全，高清${category.name}免费在线观看，即点即播不缓冲。`,
    url: `https://www.wukongmovies.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '悟空影视',
      url: 'https://www.wukongmovies.com',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `悟空影视的${category.name}可以免费观看吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `当然可以！悟空影视所有${category.name}均为高清免费在线观看，无需注册会员，即点即播，不卡顿不缓冲。`,
        },
      },
      {
        '@type': 'Question',
        name: `悟空影视${category.name}更新速度快吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `悟空影视拥有专业的影视资源团队，${category.name}新片上映后第一时间同步更新，确保您能观看到最新的${category.name}大片。`,
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-500">
                  <i className={`${category.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    悟空影视{category.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    {category.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200/70 text-foreground-600 hover:bg-background-200 hover:text-foreground-950 border border-background-300/40'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {categoryMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无该分类的电影，敬请期待！</p>
              </div>
            )}

            <div className="mt-12 p-6 md:p-8 rounded-xl bg-background-200/70 border border-background-300/40">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                关于悟空影视{category.name}
              </h2>
              <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                <p>
                  悟空影视{category.name}频道汇集了最新、最全、最热门的{category.name}资源。
                  无论您是喜欢经典{category.name}还是最新上映的{category.name}大片，
                  在这里都能找到心仪的影片。所有{category.name}均采用高清画质，支持流畅播放，
                  让您享受极致的观影体验。
                </p>
                <p>
                  悟空影视致力于为广大影迷提供优质的免费在线观影服务，
                  {category.name}频道每周持续更新，确保内容的新鲜度和丰富度。
                  无需下载、无需安装插件，打开网页即可观看，方便快捷。
                </p>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}